/**
 * 資料字典 table（REQ-20260419004：改為拖曳排序）。
 *
 * 核心改動：
 * - 移除「順序」輸入欄位與「改順序」按鈕
 * - 每 row 可拖曳；放開時 optimistic 重排 + 對有變動的 row 送 PATCH
 * - displayOrder 以 (index+1) * 10 重算，保留間距
 * - 新增的 row 自動放到最末
 *
 * 刻意不拉 dnd lib（保持 bundle 乾淨）。HTML5 native drag 在觸控裝置不支援是已知限制。
 */
import { useEffect, useMemo, useState } from 'react';
import {
  useCreateReference,
  useDeleteReference,
  useReferences,
  useUpdateReference,
  type SettingsPath,
} from './api';
import type { ReferenceRow } from '@app/shared';
import { getApiErrorMessage } from '../../lib/api-client';

export function SettingsTable({ path, label }: { path: SettingsPath; label: string }): JSX.Element {
  const { data, isLoading, isError, error } = useReferences(path, true);
  const createMutation = useCreateReference(path);
  const updateMutation = useUpdateReference(path);
  const deleteMutation = useDeleteReference(path);

  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');

  // 本地副本：用於 optimistic 拖曳重排。伺服器資料變動時同步過來（sorted by displayOrder）。
  const serverRows: ReferenceRow[] = useMemo(() => {
    const rows = data?.data ?? [];
    return [...rows].sort((a, b) => a.displayOrder - b.displayOrder || a.id - b.id);
  }, [data]);
  const [localRows, setLocalRows] = useState<ReferenceRow[]>(serverRows);
  useEffect(() => {
    setLocalRows(serverRows);
  }, [serverRows]);

  const [reordering, setReordering] = useState(false);
  const [dragId, setDragId] = useState<number | null>(null);
  const [reorderError, setReorderError] = useState<string | null>(null);

  if (isLoading) return <div role="status" className="text-slate-500">載入中...</div>;
  if (isError) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{getApiErrorMessage(error, '未知錯誤')}
      </div>
    );
  }

  const onCreate = (): void => {
    if (!newCode || !newName) return;
    // 新 row 放最末：displayOrder = max + 10
    const maxOrder = localRows.reduce(
      (m, r) => Math.max(m, r.displayOrder),
      0,
    );
    createMutation.mutate(
      { code: newCode, name: newName, displayOrder: maxOrder + 10, isActive: true },
      {
        onSuccess: () => {
          setNewCode('');
          setNewName('');
        },
      },
    );
  };

  const onDelete = (row: ReferenceRow): void => {
    if (!window.confirm(`確定要停用 ${row.name} 嗎？`)) return;
    deleteMutation.mutate(row.id);
  };

  const onToggleActive = (row: ReferenceRow): void => {
    updateMutation.mutate({ id: row.id, input: { isActive: !row.isActive } });
  };

  const onRename = (row: ReferenceRow): void => {
    const newN = window.prompt(`輸入新名稱（原：${row.name}）`, row.name);
    if (!newN || newN === row.name) return;
    updateMutation.mutate({ id: row.id, input: { name: newN } });
  };

  // ─── 拖曳 handlers ──────────────────────────────────
  const handleDragStart = (id: number) => (e: React.DragEvent<HTMLTableRowElement>): void => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    // 雖然不用 dataTransfer.setData 就能 drop（我們用 state），但部分瀏覽器要求有 data 才能啟動 drag
    e.dataTransfer.setData('text/plain', String(id));
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>): void => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (targetId: number) => (e: React.DragEvent<HTMLTableRowElement>): void => {
    e.preventDefault();
    const draggedId = dragId;
    setDragId(null);
    if (draggedId == null || draggedId === targetId) return;

    const from = localRows.findIndex((r) => r.id === draggedId);
    const to = localRows.findIndex((r) => r.id === targetId);
    if (from === -1 || to === -1) return;

    const next = [...localRows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    setLocalRows(next);
    void commitReorder(next);
  };

  const commitReorder = async (rows: ReferenceRow[]): Promise<void> => {
    setReordering(true);
    setReorderError(null);
    try {
      const patches = rows
        .map((r, i) => ({ r, newOrder: (i + 1) * 10 }))
        .filter((x) => x.newOrder !== x.r.displayOrder);

      await Promise.all(
        patches.map((p) =>
          updateMutation.mutateAsync({
            id: p.r.id,
            input: { displayOrder: p.newOrder },
          }),
        ),
      );
    } catch (err) {
      setReorderError(getApiErrorMessage(err, '更新失敗'));
      setLocalRows(serverRows); // rollback
    } finally {
      setReordering(false);
    }
  };

  return (
    <section className="grid gap-4">
      <h2 className="text-lg font-semibold">{label}</h2>

      {createMutation.isError ? (
        <div role="alert" className="text-red-600">
          建立失敗：
          {getApiErrorMessage(createMutation.error, '未知錯誤')}
        </div>
      ) : null}
      {reorderError ? (
        <div role="alert" className="text-red-600">
          排序更新失敗：{reorderError}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 items-end bg-white p-3 rounded border">
        <label className="grid text-sm">
          <span className="text-slate-600">名稱</span>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className={inputCls}
            maxLength={30}
          />
        </label>
        <label className="grid text-sm">
          <span className="text-slate-600">Code</span>
          <input
            type="text"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value.toUpperCase())}
            className={inputCls}
            maxLength={20}
            placeholder="TEST_NEW"
          />
        </label>
        <button
          type="button"
          onClick={onCreate}
          disabled={createMutation.isPending || !newCode || !newName}
          className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          新增
        </button>
        <span className="text-xs text-slate-500 ml-auto">
          拖曳左側 <span className="font-mono">⋮⋮</span> 可改排序
        </span>
      </div>

      <table
        className="w-full bg-white shadow-sm rounded-md"
        data-testid="settings-table"
      >
        <thead>
          <tr className="border-b text-left text-sm text-slate-600">
            <th className="px-2 py-2 w-8"></th>
            <th className="px-4 py-2">名稱</th>
            <th className="px-4 py-2">Code</th>
            <th className="px-4 py-2">啟用</th>
            <th className="px-4 py-2">動作</th>
          </tr>
        </thead>
        <tbody>
          {localRows.map((r) => (
            <tr
              key={r.id}
              draggable
              onDragStart={handleDragStart(r.id)}
              onDragOver={handleDragOver}
              onDrop={handleDrop(r.id)}
              className={`border-b last:border-0 hover:bg-slate-50 ${
                dragId === r.id ? 'opacity-50' : ''
              } ${reordering ? 'pointer-events-none' : ''}`}
              data-row-id={r.id}
              data-testid={`settings-row-${r.code}`}
            >
              <td
                className="px-2 py-2 text-slate-400 cursor-move select-none text-center"
                aria-label="拖曳排序"
              >
                ⋮⋮
              </td>
              <td className="px-4 py-2">{r.name}</td>
              <td className="px-4 py-2 font-mono text-sm">{r.code}</td>
              <td className="px-4 py-2">{r.isActive ? '✔' : '—'}</td>
              <td className="px-4 py-2 flex gap-2 text-sm">
                <button type="button" onClick={() => onRename(r)} className="text-blue-600 hover:underline">
                  改名
                </button>
                <button
                  type="button"
                  onClick={() => onToggleActive(r)}
                  className="text-blue-600 hover:underline"
                >
                  {r.isActive ? '停用' : '啟用'}
                </button>
                {r.isActive ? (
                  <button type="button" onClick={() => onDelete(r)} className="text-red-600 hover:underline">
                    刪除
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
          {localRows.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-4 py-4 text-center text-slate-500">
                尚無資料
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}

const inputCls =
  'w-40 border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';
